import {
  assertDockLayout,
  createDockLayout,
  createDockSplitNode,
  createDockTabNode,
  DOCK_NODE_TYPES,
} from "./DockLayout";
import {
  assertDockPanelRegistry,
} from "./DockPanelRegistry";
import {
  DockingRuntimeError,
} from "./errors";


function updateNode(node, nodeId, updater) {
  if (node.id === nodeId) {
    return updater(node);
  }

  if (node.type !== DOCK_NODE_TYPES.SPLIT) {
    return node;
  }

  let changed = false;

  const children = node.children.map((child) => {
    const updatedChild = updateNode(
      child,
      nodeId,
      updater,
    );

    if (updatedChild !== child) {
      changed = true;
    }

    return updatedChild;
  });

  if (!changed) {
    return node;
  }

  return createDockSplitNode({
    ...node,
    children,
  });
}


function mapNodes(node, updater) {
  const updatedNode = updater(node);

  if (updatedNode.type !== DOCK_NODE_TYPES.SPLIT) {
    return updatedNode;
  }

  return createDockSplitNode({
    ...updatedNode,
    children: updatedNode.children.map(
      (child) => mapNodes(child, updater),
    ),
  });
}


function containsPanel(node, panelId) {
  if (node.type === DOCK_NODE_TYPES.PANEL) {
    return node.panelId === panelId;
  }

  if (node.type === DOCK_NODE_TYPES.TABS) {
    return node.panelIds.includes(panelId);
  }

  return node.children.some(
    (child) => containsPanel(child, panelId),
  );
}


function removePanelFromNode(node, panelId) {
  if (node.type === DOCK_NODE_TYPES.PANEL) {
    return node.panelId === panelId
      ? null
      : node;
  }

  if (node.type === DOCK_NODE_TYPES.TABS) {
    if (!node.panelIds.includes(panelId)) {
      return node;
    }

    const panelIds = node.panelIds.filter(
      (candidate) => candidate !== panelId,
    );

    if (panelIds.length === 0) {
      return null;
    }

    return createDockTabNode({
      ...node,
      panelIds,
      activePanelId:
        node.activePanelId === panelId
          ? panelIds[0]
          : node.activePanelId,
    });
  }

  const children = node.children
    .map(
      (child) => removePanelFromNode(
        child,
        panelId,
      ),
    )
    .filter(Boolean);

  if (children.length === 0) {
    return null;
  }

  if (children.length === 1) {
    return children[0];
  }

  return createDockSplitNode({
    ...node,
    children,
    sizes: children.map(
      () => 100 / children.length,
    ),
  });
}


export function createDockingRuntime({
  registry,
  layout,
} = {}) {
  const validatedRegistry =
    assertDockPanelRegistry(registry);
  const initialLayout = assertDockLayout(layout);

  let currentLayout = initialLayout;
  const listeners = new Set();

  function assertKnownPanels(nextLayout) {
    const panelIds = [];

    function collect(node) {
      if (node.type === DOCK_NODE_TYPES.PANEL) {
        panelIds.push(node.panelId);
        return;
      }

      if (node.type === DOCK_NODE_TYPES.TABS) {
        panelIds.push(...node.panelIds);
        return;
      }

      node.children.forEach(collect);
    }

    collect(nextLayout.root);
    panelIds.push(...nextLayout.hiddenPanelIds);

    const unknownPanelId = panelIds.find(
      (panelId) => !validatedRegistry.has(panelId),
    );

    if (unknownPanelId) {
      throw new DockingRuntimeError(
        `DockLayout references an unknown panel: ${unknownPanelId}.`,
        "UNKNOWN_DOCK_PANEL",
      );
    }

    return nextLayout;
  }

  assertKnownPanels(initialLayout);

  function notify() {
    for (const listener of Array.from(listeners)) {
      listener();
    }
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new DockingRuntimeError(
        "DockingRuntime.subscribe() requires a function.",
        "INVALID_DOCKING_LISTENER",
      );
    }

    listeners.add(listener);

    return function unsubscribe() {
      return listeners.delete(listener);
    };
  }

  function getLayout() {
    return currentLayout;
  }

  function setLayout(nextLayout) {
    const validatedLayout = assertKnownPanels(
      assertDockLayout(nextLayout),
    );

    if (
      JSON.stringify(validatedLayout)
      === JSON.stringify(currentLayout)
    ) {
      return currentLayout;
    }

    currentLayout = validatedLayout;
    notify();

    return currentLayout;
  }

  function replaceRoot(root, hiddenPanelIds = currentLayout.hiddenPanelIds) {
    return setLayout(
      createDockLayout({
        ...currentLayout,
        root,
        hiddenPanelIds,
      }),
    );
  }

  function activatePanel(panelId) {
    if (!validatedRegistry.has(panelId)) {
      throw new DockingRuntimeError(
        `Unknown dock panel: ${panelId}.`,
        "UNKNOWN_DOCK_PANEL",
      );
    }

    let found = false;

    const root = mapNodes(
      currentLayout.root,
      (node) => {
        if (
          node.type === DOCK_NODE_TYPES.TABS
          && node.panelIds.includes(panelId)
        ) {
          found = true;

          if (node.activePanelId === panelId) {
            return node;
          }

          return createDockTabNode({
            ...node,
            activePanelId: panelId,
          });
        }

        if (
          node.type === DOCK_NODE_TYPES.PANEL
          && node.panelId === panelId
        ) {
          found = true;
        }

        return node;
      },
    );

    if (!found) {
      throw new DockingRuntimeError(
        `Panel is not visible in the layout: ${panelId}.`,
        "DOCK_PANEL_NOT_VISIBLE",
      );
    }

    return replaceRoot(root);
  }

  function resizeSplit(splitId, sizes) {
    const root = updateNode(
      currentLayout.root,
      splitId,
      (node) => {
        if (node.type !== DOCK_NODE_TYPES.SPLIT) {
          throw new DockingRuntimeError(
            `Dock node is not a split: ${splitId}.`,
            "DOCK_NODE_NOT_SPLIT",
          );
        }

        return createDockSplitNode({
          ...node,
          sizes,
        });
      },
    );

    return replaceRoot(root);
  }

  function hidePanel(panelId) {
    const panel = validatedRegistry.get(panelId);

    if (!panel) {
      throw new DockingRuntimeError(
        `Unknown dock panel: ${panelId}.`,
        "UNKNOWN_DOCK_PANEL",
      );
    }

    if (!panel.hideable) {
      throw new DockingRuntimeError(
        `Dock panel cannot be hidden: ${panelId}.`,
        "DOCK_PANEL_NOT_HIDEABLE",
      );
    }

    if (!containsPanel(currentLayout.root, panelId)) {
      return currentLayout;
    }

    const root = removePanelFromNode(
      currentLayout.root,
      panelId,
    );

    if (!root) {
      throw new DockingRuntimeError(
        "DockingRuntime cannot hide the last visible panel.",
        "LAST_VISIBLE_DOCK_PANEL",
      );
    }

    return replaceRoot(
      root,
      [
        ...currentLayout.hiddenPanelIds,
        panelId,
      ],
    );
  }

  function closePanel(panelId) {
    const panel = validatedRegistry.get(panelId);

    if (!panel) {
      throw new DockingRuntimeError(
        `Unknown dock panel: ${panelId}.`,
        "UNKNOWN_DOCK_PANEL",
      );
    }

    if (!panel.closable) {
      throw new DockingRuntimeError(
        `Dock panel cannot be closed: ${panelId}.`,
        "DOCK_PANEL_NOT_CLOSABLE",
      );
    }

    if (!containsPanel(currentLayout.root, panelId)) {
      return currentLayout;
    }

    const root = removePanelFromNode(
      currentLayout.root,
      panelId,
    );

    if (!root) {
      throw new DockingRuntimeError(
        "DockingRuntime cannot close the last visible panel.",
        "LAST_VISIBLE_DOCK_PANEL",
      );
    }

    return replaceRoot(root);
  }

  function restorePanel(panelId, targetTabId) {
    if (!currentLayout.hiddenPanelIds.includes(panelId)) {
      return currentLayout;
    }

    const root = updateNode(
      currentLayout.root,
      targetTabId,
      (node) => {
        if (node.type !== DOCK_NODE_TYPES.TABS) {
          throw new DockingRuntimeError(
            `Restore target is not a tab group: ${targetTabId}.`,
            "DOCK_RESTORE_TARGET_NOT_TABS",
          );
        }

        return createDockTabNode({
          ...node,
          panelIds: [
            ...node.panelIds,
            panelId,
          ],
          activePanelId: panelId,
        });
      },
    );

    return replaceRoot(
      root,
      currentLayout.hiddenPanelIds.filter(
        (candidate) => candidate !== panelId,
      ),
    );
  }

  function movePanel(panelId, targetTabId, targetIndex = null) {
    const panel = validatedRegistry.get(panelId);

    if (!panel) {
      throw new DockingRuntimeError(
        `Unknown dock panel: ${panelId}.`,
        "UNKNOWN_DOCK_PANEL",
      );
    }

    if (!panel.movable) {
      throw new DockingRuntimeError(
        `Dock panel cannot be moved: ${panelId}.`,
        "DOCK_PANEL_NOT_MOVABLE",
      );
    }

    if (!containsPanel(currentLayout.root, panelId)) {
      throw new DockingRuntimeError(
        `Panel is not visible: ${panelId}.`,
        "DOCK_PANEL_NOT_VISIBLE",
      );
    }

    const withoutPanel = removePanelFromNode(
      currentLayout.root,
      panelId,
    );

    if (!withoutPanel) {
      throw new DockingRuntimeError(
        "DockingRuntime cannot move the last visible panel.",
        "LAST_VISIBLE_DOCK_PANEL",
      );
    }

    let targetFound = false;

    const root = updateNode(
      withoutPanel,
      targetTabId,
      (node) => {
        if (node.type !== DOCK_NODE_TYPES.TABS) {
          throw new DockingRuntimeError(
            `Move target is not a tab group: ${targetTabId}.`,
            "DOCK_MOVE_TARGET_NOT_TABS",
          );
        }

        targetFound = true;

        const panelIds = [...node.panelIds];
        const index = targetIndex === null
          ? panelIds.length
          : Math.max(
            0,
            Math.min(
              Number(targetIndex),
              panelIds.length,
            ),
          );

        panelIds.splice(index, 0, panelId);

        return createDockTabNode({
          ...node,
          panelIds,
          activePanelId: panelId,
        });
      },
    );

    if (!targetFound) {
      throw new DockingRuntimeError(
        `Unknown target tab group: ${targetTabId}.`,
        "UNKNOWN_DOCK_TAB_GROUP",
      );
    }

    return replaceRoot(root);
  }

  function resetLayout() {
    return setLayout(initialLayout);
  }

  return Object.freeze({
    getLayout,
    setLayout,
    activatePanel,
    movePanel,
    resizeSplit,
    closePanel,
    hidePanel,
    restorePanel,
    resetLayout,
    subscribe,
  });
}


export function isDockingRuntime(value) {
  return Boolean(
    value
    && typeof value === "object"
    && typeof value.getLayout === "function"
    && typeof value.setLayout === "function"
    && typeof value.activatePanel === "function"
    && typeof value.movePanel === "function"
    && typeof value.resizeSplit === "function"
    && typeof value.closePanel === "function"
    && typeof value.hidePanel === "function"
    && typeof value.restorePanel === "function"
    && typeof value.resetLayout === "function"
    && typeof value.subscribe === "function"
  );
}


export function assertDockingRuntime(value) {
  if (!isDockingRuntime(value)) {
    throw new DockingRuntimeError(
      "Expected a valid DockingRuntime.",
      "INVALID_DOCKING_RUNTIME",
    );
  }

  return value;
}
